import { useRef } from 'react';
import { useIntl } from 'react-intl';

import {
  CollectionAsset,
  CollectionProgramAsset,
  isCollectionAssetPurchasable,
  isCollectionChannelAsset,
  isCollectionLinkAsset,
  isCollectionLinkAssetValid,
  isCollectionProgramAsset,
  isCollectionSeriesAsset,
  isLiveProgram,
  shouldPlayOnExternalApp,
  useLinears,
} from '@kaltura-ott/tvpil-shared';

import { useMouseEnterHandle } from 'components/presenters/Card/hooks/useMouseEnterHandle/useMouseEnterHandle';
import { useMouseLeaveHandle } from 'components/presenters/Card/hooks/useMouseLeaveHandle/useMouseLeaveHandle';
import { useCardLink } from 'components/widgets/CardView/hooks/useCardLink/useCardLink';
import { useRailContext } from 'components/widgets/Rail/components/RailProvider/RailProvider';
import { useWidgetContext } from 'context/widgetProvider/widgetProvider';
import { useRecordRepository } from 'hooks';
import { getAriaLabelForAsset } from 'utils';

import { useClickHandler } from '../useClickHandler/useClickHandler';
import { useHandleClickInfo } from '../useHandleClickInfo';
import { useRecommendationCardImages } from '../useRecommendationCardImages';
import { useRecommendationCardRating } from '../useRecommendationCardRating/useRecommendationCardRating';
import { useRecommendationCardRef } from '../useRecommendationCardRef';
import { useRecommendationChannelInfo } from '../useRecommendationChannelInfo/useRecommendationChannelInfo';

interface Props {
  item: CollectionAsset;
  itemWidth: number;
  imageHeight: number;
}

function useRecommendationCard({ item, itemWidth, imageHeight }: Props) {
  const intl = useIntl();
  const { linears } = useLinears(); // TODO: must be replaced by useChannelsDictionary
  const isLinkAsAsset = isCollectionLinkAsset(item);
  const isValidLinkAsAsset = isLinkAsAsset && isCollectionLinkAssetValid(item);
  const isProgramAsset = isCollectionProgramAsset(item);
  const isCatchupSeries = isProgramAsset && Boolean(item.seriesId);
  const isFutureProgram = isProgramAsset && item.startDate > Date.now() && !isCatchupSeries;
  const isPurchasable = isCollectionAssetPurchasable(item, linears);
  const isTypeOfSeries = isCollectionSeriesAsset(item);
  const isAsPlayAsset = !isTypeOfSeries && !isFutureProgram && !isPurchasable && !isLinkAsAsset;
  const isChannelAsset = isCollectionChannelAsset(item);
  const isProgramLive = isProgramAsset && isLiveProgram(item);

  // using version v2 of the rail provider
  const { onHover, nextButtons } = useRailContext();
  const {
    settings,
    settings: { shouldShowRating },
    type: collectionType,
    imageWidth,
    isGrid,
  } = useWidgetContext();

  const { link } = useCardLink({
    data: item,
    isSingleVod: false,
    isFutureProgram,
    collectionType,
    isLinkAsAsset,
    isAsPlayAsset,
    isChannelAsset,
    isProgramLive,
  });

  // adults
  const isAdult = !!item?.isAdult;
  // refs
  const handleLinkRef = useRef(null);
  const { ref, handleRef } = useRecommendationCardRef(itemWidth, imageHeight);
  // handlers
  const onMouseEnter = useMouseEnterHandle({ ref, itemWidth, imageHeight, isGrid, onHover, nextButtons });
  const onMouseLeave = useMouseLeaveHandle(onHover);
  const isAssetPlayOnExternalApp = shouldPlayOnExternalApp(item);
  const clickHandler = useClickHandler({
    data: item,
    isLinkAsAsset,
    isValidLinkAsAsset,
    isChannelHasNoProgram: false,
    mediaTypeParam: item.type,
    isPurchasable,
    imageHeight,
    link,
  });
  const { handleClickInfo } = useHandleClickInfo({
    id: item?.id!,
    externalId: undefined,
    mediaTypeParam: item.type,
    isChannelHasNoProgram: false,
  });
  // rating
  const { isDisplayRating, assetRating } = useRecommendationCardRating({ shouldShowRating, item });
  // images urls
  const { backgroundImageUrl, defaultImageUrl } = useRecommendationCardImages({
    item,
    imageType: settings.imageType,
    imageWidth,
    imageHeight,
  });
  // title and channelName
  const { title, channelName } = useRecommendationChannelInfo(item);
  // collectionProgramAsset type checking for AirTime presentation
  const collectionProgramAsset = isProgramAsset ? item : undefined;
  // checking for seriesId
  const seriesId = isProgramAsset ? item.seriesId : undefined;

  const { providerName } = item as CollectionProgramAsset;
  const isExternalProviderAsset = Boolean(providerName) && isAssetPlayOnExternalApp;

  // For record indication
  const recordRepository = useRecordRepository();
  const record = item?.id ? recordRepository.records.get(item.id.toString()) : null;
  const seriesRecord = seriesId ? recordRepository.seriesRecords.get(seriesId) : null;
  const isRecord = Boolean(record || seriesRecord);
  const isSeriesRecord = Boolean(seriesRecord);
  //

  // getting aria-label text
  const ariaLabel = getAriaLabelForAsset({
    intl,
    title,
    channelName,
    isRestricted: false,
    isRecord,
    shouldPronouncedAiringTime: false,
    isSeriesRecord
  });

  return {
    link,
    title,
    isAdult,
    isRecord,
    handleRef,
    ariaLabel,
    assetRating,
    channelName,
    clickHandler,
    onMouseEnter,
    onMouseLeave,
    handleLinkRef,
    isLinkAsAsset,
    isSeriesRecord,
    handleClickInfo,
    isDisplayRating,
    defaultImageUrl,
    backgroundImageUrl,
    collectionProgramAsset,
    isExternalProviderAsset,
  };
}

export { useRecommendationCard };
